import type { Component } from '../router';
import { HEALTH_EVENT, checkHealth, getLastHealth, type HealthState } from '../api/client';
import { t, type StringKey } from '../i18n';

// Catalogue KEYS, not resolved strings: a module-eval t() froze these to the
// import-time locale (before a lazy es/pt chunk registered) and no language switch
// could recompute them. Both consumers resolve at render — render() runs on every
// health update as well as on mount, so the label follows a live locale change even
// between remounts.
const LABEL: Record<HealthState['kind'], StringKey> = {
  unknown: 'health.checking',
  ok: 'health.online',
  degraded: 'health.degraded',
  error: 'health.offline',
};

function detailText(state: HealthState): string {
  if (state.kind === 'ok' || state.kind === 'degraded') {
    return t('health.detail', {
      status: state.health.couchbase.status,
      ms: state.health.couchbase.latencyMs,
    });
  }
  return t(LABEL[state.kind]);
}

// Cross-cutting system-health indicator. Reads shared state from the API
// client and reflects live updates via the HEALTH_EVENT bus; the app shell
// mounts one instance per viewport (desktop top nav + phone chip).
export class HealthPill implements Component {
  private el: HTMLElement | null = null;
  private label: HTMLElement | null = null;

  private readonly onChange = (e: Event): void => {
    this.render((e as CustomEvent<HealthState>).detail);
  };

  mount(host: HTMLElement): void {
    const el = document.createElement('span');
    el.className = 'healthpill';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    const dot = document.createElement('span');
    dot.className = 'healthpill__dot';
    dot.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'healthpill__label';

    el.append(dot, label);
    host.appendChild(el);

    this.el = el;
    this.label = label;
    this.render(getLastHealth());

    window.addEventListener(HEALTH_EVENT, this.onChange);
    void checkHealth();
  }

  unmount(): void {
    window.removeEventListener(HEALTH_EVENT, this.onChange);
    this.el?.remove();
    this.el = null;
    this.label = null;
  }

  private render(state: HealthState): void {
    if (!this.el || !this.label) return;
    this.el.dataset.state = state.kind;
    const label = t(LABEL[state.kind]);
    this.label.textContent = label;
    const detail = detailText(state);
    this.el.setAttribute('aria-label', t('health.ariaStatus', { label, detail }));
    this.el.title = detail;
  }
}
